import { Button, Flex, Text, Grid, Card, Box, Badge, Code } from "@radix-ui/themes";
import { CheckCircle2, Clock } from "lucide-react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import CheckboxField from "@/components/form/CheckboxField";
import { BILLING_INTERVALS, PAYMENT_TYPES } from "@/types/breeze/AnythinkPay";
import { useAuth } from "@/providers/AuthProvider";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateSubscriptionPlanRequest,
  SubscriptionPlan,
} from "@/types/breeze/AnythinkPay";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import ErrorCard from "@/components/ui/ErrorCard";

const validationSchema = Yup.object({
  plan_name: Yup.string().required("Plan name is required"),
  name: Yup.string().required("Subscription name is required"),
  type: Yup.string().required("Type is required"),
  amount: Yup.number()
    .positive("Amount must be positive")
    .required("Amount is required")
    .min(0.01, "Amount must be at least 0.01"),
  billing_interval: Yup.string().required("Billing interval is required"),
  interval_count: Yup.number()
    .positive("Interval count must be positive")
    .integer("Interval count must be a whole number")
    .required("Interval count is required")
    .min(1, "Interval count must be at least 1"),
  trial_period_days: Yup.number()
    // Empty field = no trial. Coerce "" → null so the optional/nullable rules apply instead of a
    // "cannot be null" type error (the field's own help text says "leave empty for no trial period").
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? null : value,
    )
    .nullable()
    .positive("Trial period must be positive")
    .integer("Trial period must be a whole number")
    .max(365, "Trial period cannot exceed 365 days"),
});

type FormValues = CreateSubscriptionPlanRequest;

const defaultInitialValues: FormValues = {
  plan_name: "",
  name: "",
  description: "",
  type: "web",
  amount: 0,
  currency: "gbp",
  billing_interval: "month",
  interval_count: 1,
  trial_period_days: 0,
  product_name: "",
  product_description: "",
  reference: "",
  is_active: true,
  apple_product_id: "",
  apple_subscription_group_id: "",
  tier_rank: null,
};

function planToFormValues(p: SubscriptionPlan): FormValues {
  return {
    plan_name: p.plan_name,
    name: p.name,
    description: p.description ?? "",
    type: p.type,
    amount: p.amount,
    currency: p.currency,
    billing_interval: p.billing_interval,
    interval_count: p.interval_count,
    trial_period_days: p.trial_period_days,
    product_name: p.product_name ?? "",
    product_description: p.product_description ?? "",
    reference: p.reference ?? "",
    is_active: p.is_active,
    apple_product_id: p.apple_product_id ?? "",
    apple_subscription_group_id: p.apple_subscription_group_id ?? "",
    tier_rank: p.tier_rank ?? null,
  };
}

interface SubscriptionPlanFormProps {
  planId?: number;
  plan?: SubscriptionPlan;
}

export default function SubscriptionPlanForm({
  planId,
  plan: planProp,
}: SubscriptionPlanFormProps) {
  const isEditMode = planId !== undefined;
  const { safeNavigate, currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: planQuery,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["subscription-plan", planId],
    queryFn: () => anythinkPayService.getSubscriptionPlan(planId!),
    enabled: isEditMode && Number.isInteger(planId) && !planProp,
  });

  const plan = planProp ?? planQuery;

  const plansListPath = `/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions?tab=plans`;

  const createPlan = useMutation({
    mutationFn: (data: CreateSubscriptionPlanRequest) =>
      anythinkPayService.createSubscriptionPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      safeNavigate(plansListPath);
    },
  });

  const updatePlan = useMutation({
    mutationFn: (data: CreateSubscriptionPlanRequest) =>
      anythinkPayService.updateSubscriptionPlan(planId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      queryClient.invalidateQueries({
        queryKey: ["subscription-plan", planId],
      });
      safeNavigate(plansListPath);
    },
  });

  const handleCancel = () => {
    safeNavigate(plansListPath);
  };

  const handleSubmit = (values: FormValues) => {
    const submitData = {
      ...values,
      product_name: values.product_name || undefined,
      product_description: values.product_description || undefined,
      reference: values.reference || undefined,
      apple_product_id: values.apple_product_id || undefined,
      apple_subscription_group_id: values.apple_subscription_group_id || undefined,
      tier_rank:
        values.tier_rank === null || values.tier_rank === undefined || values.tier_rank === ("" as unknown as number)
          ? null
          : Number(values.tier_rank),
      trial_period_days: values.trial_period_days
        ? Number(values.trial_period_days)
        : undefined,
    };

    if (isEditMode) {
      updatePlan.mutate(submitData);
    } else {
      createPlan.mutate(submitData);
    }
  };

  if (isEditMode) {
    if (!planProp) {
      if (isLoading) return <LoadingScreen />;
      if (error) return <ErrorCard error={error.message} />;
    }
    if (!plan) return null;
  }

  const initialValues = isEditMode
    ? planToFormValues(plan!)
    : defaultInitialValues;

  const isPending = isEditMode ? updatePlan.isPending : createPlan.isPending;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={isEditMode}
      onSubmit={handleSubmit}
    >
      <Form>
        <Flex direction="column" gap="4">
          <InputField
            name="plan_name"
            label="Plan name"
            required
            type="text"
            description="Admin-facing label for this plan (e.g. 'Premium Monthly', 'Getahead Annual'). Used to identify the plan when adding subscribers."
          />

          <Text size="4" weight="bold">
            Subscription defaults
          </Text>

          <SelectField
            name="type"
            label="Type"
            required
            type="string"
            description="Where this subscription will be used. 'Web' creates subscriptions with Stripe Checkout (best for browser payments). 'Mobile' is for mobile-native payments."
          >
            {PAYMENT_TYPES.map((t) => (
              <SelectField.Item key={t.value} value={t.value}>
                {t.label}
              </SelectField.Item>
            ))}
          </SelectField>

          <InputField
            name="name"
            label="Subscription name"
            required
            type="text"
            description="Customer-facing name for the subscription product (e.g. Gold Plan). Shown to users at checkout and on invoices."
          />
          <InputField
            name="description"
            label="Description"
            as="textarea"
            options={{ rows: 3 }}
            description="Describe the features or benefits included in this plan. Visible to users during checkout."
          />

          <InputField
            name="amount"
            label="Amount (GBP £)"
            required
            type="number"
            step="0.01"
            min="0.01"
            description="Amount to charge per billing interval (in GBP £). Enter the price excluding VAT."
          />

          <Grid columns="2" gap="4">
            <SelectField
              name="billing_interval"
              label="Billing period"
              required
              type="string"
              description="The unit of time for billing — day, week, month, or year."
            >
              {BILLING_INTERVALS.map((i) => (
                <SelectField.Item key={i.value} value={i.value}>
                  {i.label}
                </SelectField.Item>
              ))}
            </SelectField>
            <InputField
              name="interval_count"
              label="Bill every"
              required
              type="number"
              min="1"
              description="How many billing periods between each charge. 1 = standard monthly/yearly, 3 = every 3 months, etc."
            />
          </Grid>

          <InputField
            name="trial_period_days"
            label="Trial period (days)"
            type="number"
            min="1"
            max="365"
            description="Number of free days before billing starts. Leave empty for no trial period."
          />

          <InputField
            name="product_name"
            label="Product name (optional)"
            type="text"
            description="If your subscription creates a Stripe Product, override the product name here. Leave blank to use the subscription name."
          />
          <InputField
            name="product_description"
            label="Product description (optional)"
            as="textarea"
            options={{ rows: 2 }}
            description="Optional description for the associated Stripe Product. Useful for more detailed product info."
          />
          <InputField
            name="reference"
            label="Reference (optional)"
            type="text"
            description="Custom reference for your records or reporting. Never shown to customers."
          />

          <InputField
            name="apple_product_id"
            label="Apple product ID (optional)"
            type="text"
            description="App Store Connect product identifier for the iOS in-app purchase rail (e.g. com.yourcompany.app.premium_monthly). Leave blank if this plan isn't sold via the iOS app."
          />

          <InputField
            name="apple_subscription_group_id"
            label="Apple subscription group ID (optional)"
            type="text"
            description="App Store Connect subscription group identifier. Plans in the same group are interchangeable on iOS (switching is a crossgrade). Auto-filled from the first verified purchase if left blank."
          />

          <InputField
            name="tier_rank"
            label="Tier rank (optional)"
            type="number"
            min="1"
            max="100"
            step="1"
            description="Optional ordinal used by promo code eligibility to gate premium-tier-only codes. Higher = higher tier (silver=1, gold=2, platinum=3). Leave blank if you don't use tiered access."
          />

          <CheckboxField
            name="is_active"
            label="Active"
            description="Active plans appear in the Add Subscriber picker. Retired plans (unchecked) stay billing existing subscribers but can't be subscribed to anew."
          />

          {isEditMode && plan && (
            <Card>
              <Box p="3">
                <Flex align="center" gap="2" mb="2">
                  {plan.stripe_price_id ? (
                    <>
                      <CheckCircle2 size={16} color="var(--green-9)" />
                      <Text size="2" weight="medium">Synced to Stripe</Text>
                      <Badge color="green">live</Badge>
                    </>
                  ) : (
                    <>
                      <Clock size={16} color="var(--orange-9)" />
                      <Text size="2" weight="medium">Not yet synced to Stripe</Text>
                      <Badge color="orange">draft</Badge>
                    </>
                  )}
                </Flex>
                {plan.stripe_price_id ? (
                  <Flex direction="column" gap="1">
                    <Flex gap="2" align="center">
                      <Text size="1" color="gray" style={{ minWidth: 90 }}>Product</Text>
                      <Code size="1">{plan.stripe_product_id}</Code>
                    </Flex>
                    <Flex gap="2" align="center">
                      <Text size="1" color="gray" style={{ minWidth: 90 }}>Price</Text>
                      <Code size="1">{plan.stripe_price_id}</Code>
                    </Flex>
                  </Flex>
                ) : (
                  <Text size="1" color="gray" as="p">
                    Either Stripe Connect isn't onboarded yet, or the sync failed when this plan was saved. Save the plan again to retry.
                  </Text>
                )}
              </Box>
            </Card>
          )}

          <Flex gap="2" justify="end" mt="4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              {isEditMode ? "Save plan" : "Create plan"}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Formik>
  );
}
